import React, { useState, useEffect, useRef } from 'react';
// ⚠ MODIFIED FROM PORT — these two lines bring real-backend wiring into App.
// The port script does not generate them; re-running it preserves this file
// only if you add it to scripts/port-prototype.mjs's keep-list.
import { api, subscribeSlow } from '../api.js';
import {
  getCurrentToken, getCurrentContext, getStaffProfile, onSignIn,
  signOut, readPwLinkFromUrl,
  isPortalSessionConfirmed, confirmPortalSession, hasVendorToken,
  shouldLandOnKyc, clearKycLanding,
} from '../auth.js';
import { BANKS, BILLING_STATUS_META, CATEGORIES, MY_BILLS, PENDING_APPROVALS, SUPPLIERS, bankAccountDigits, bankAccountPlaceholder, fmtTHB, netPayableOf, parseMoney } from './data.jsx';
import { MoneyInput } from './money-input.jsx';
import { IconBell, IconBuilding, IconChart, IconCheckCirc, IconChevron, IconChevronDn, IconClock, IconFile, IconList, IconLogout, IconReceipt, IconSearch, IconSettings, IconShield, IconStar, IconUpload, IconUsers } from './icons.jsx';
import { NotificationsMenu, Popover, SettingsMenu } from './menus.jsx';
import { ConfirmModal, InviteModal, Modal, ModalFooter, ModalHeader, PayRunModal, ShortcutsModal } from './modals.jsx';
import { ScreenAudit, ScreenReports } from './screens-admin.jsx';
import { ScreenBillingInbox, ScreenCompanyProfile, ScreenDocuments, ScreenSettings } from './screens-extra.jsx';
import { Avatar, ScreenApproval, ScreenDatabase, ScreenDetail, ScreenQuality, ScreenRegister, StatusPill } from './screens-internal.jsx';
import { ScreenPortalBilling, ScreenSubmit } from './screens-portal.jsx';
// ⚠ NEW — unified login surface (Claude Design handover, 2026-05-31)
import { ForgotScreen, LoginScreen, PortalWelcomeScreen, SetPasswordScreen } from './login.jsx';
import { TweakColor, TweakRadio, TweakSection, TweaksPanel, useTweaks } from './tweaks-panel.jsx';
import { useT, useLang } from '../i18n.js';
import { installColumnResize } from '../colresize.js';

import { useState as useStateApp, useEffect as useEffectApp } from 'react';

const INTERNAL_NAV = [
  { id: "database", label: "Suppliers",         icon: IconUsers,    count: SUPPLIERS.length },
  { id: "approval", label: "Approval queue",    icon: IconCheckCirc, count: PENDING_APPROVALS.length, badge: true },
  { id: "quality",  label: "Quality reviews",   icon: IconStar      },
  { id: "billing",  label: "Billing inbox",     icon: IconReceipt,  count: 12 },
];
const INTERNAL_NAV_2 = [
  // ⚠ MODIFIED FROM PORT — Compliance removed at user request (not needed yet)
  { id: "audit",      label: "Audit log",      icon: IconClock  },
  { id: "reports",    label: "Reports",        icon: IconChart  },
];

const PORTAL_NAV = [
  { id: "p-dashboard", label: "Dashboard",     icon: IconChart  },
  { id: "p-submit",    label: "Submit invoice",icon: IconUpload },
  { id: "p-billing",   label: "My billing",    icon: IconReceipt, count: MY_BILLS.length },
];
const PORTAL_NAV_2 = [
  { id: "p-profile",   label: "Company profile",icon: IconBuilding },
  { id: "p-docs",      label: "Documents",     icon: IconFile },
  { id: "p-settings",  label: "Settings",      icon: IconSettings },
];

// Tweaks panel is hidden in prod unless explicitly enabled via env.
// Dev (`npm run dev`) shows it by default for theme + demo testing.
const SHOW_TWEAKS = import.meta.env.DEV || import.meta.env.VITE_SHOW_TWEAKS === 'true';

const STATUS_LABEL = {
  'pm-review':        'PM review',
  'finance-verify':   'Finance verify',
  'approved-payment': 'Approved for payment',
  'scheduled':        'Scheduled',
  'paid':             'Paid',
  'rejected':         'Rejected',
  'disputed':         'Disputed',
};

const SCREEN_TITLE = {
  database: "Suppliers",
  detail:   "Supplier detail",
  register: "Register supplier",
  approval: "Approval queue",
  quality:  "Quality reviews",
  billing:  "Billing inbox",
  compliance: "Compliance",
  audit:    "Audit log",
  reports:  "Reports",
  "p-dashboard": "Dashboard",
  "p-submit":   "Submit invoice",
  "p-billing":  "My billing",
  "p-profile":  "Company profile",
  "p-docs":     "Documents",
  "p-settings": "Settings",
};

const ACCENTS = {
  "#9437FF": { dark: "#9437FF", light: "#5B2D9A", text: "#fff" }, // brand purple
  "#73FDFF": { dark: "#73FDFF", light: "#007A8C", text: "#000" }, // ice cyan
  "#22D389": { dark: "#22D389", light: "#0D9D5F", text: "#000" }, // emerald
  "#FFB547": { dark: "#FFB547", light: "#B87B14", text: "#000" }, // amber
};

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "theme": "dark",
  "density": "comfortable",
  "accent": "#9437FF"
}/*EDITMODE-END*/;

// ── Per-user appearance persistence ───────────────────────────────────────────
// `useTweaks` keeps theme/density/accent in memory only — it posts changes to a
// design-editor host that isn't present in production — so every reload reverted
// to TWEAK_DEFAULTS. Persist them in localStorage, keyed per signed-in user, so
// each person keeps their own appearance. `_last` is the device-level fallback
// applied before we know who is logged in (and on the login screen), so a
// returning user sees their theme on first paint with no flash.
const TWEAK_PREFS_NS = 'vendoros:appearance:';
const VALID_THEME   = { light: 1, dark: 1 };
const VALID_DENSITY = { compact: 1, comfortable: 1 };

function readTweakPrefs(storeKey) {
  try {
    const o = JSON.parse(localStorage.getItem(TWEAK_PREFS_NS + storeKey) || 'null');
    if (!o || typeof o !== 'object') return null;
    const out = {};
    if (VALID_THEME[o.theme])     out.theme   = o.theme;
    if (VALID_DENSITY[o.density]) out.density = o.density;
    if (ACCENTS[o.accent])        out.accent  = o.accent;   // stale accent → ignore
    return Object.keys(out).length ? out : null;
  } catch (e) { return null; }
}
function writeTweakPrefs(storeKey, vals) {
  try {
    localStorage.setItem(TWEAK_PREFS_NS + storeKey, JSON.stringify({
      theme: vals.theme, density: vals.density, accent: vals.accent,
    }));
  } catch (e) { /* private mode / quota — appearance just won't persist */ }
}
// Stable per-user key: staff email (lower-cased), else portal vendor id.
function tweakUserKey(staffProfile, portalVendor) {
  const email = staffProfile && staffProfile.email;
  if (email) return 'staff:' + String(email).toLowerCase();
  const vid = portalVendor && portalVendor.vendor_id;
  if (vid) return 'vendor:' + vid;
  return null;
}

export default function App() {
  const t = useT();
  const { lang, setLang } = useLang();
  // Seed from the device's last-used appearance so the first paint (incl. the
  // login screen) matches; the signed-in user's own prefs load right after.
  const initialTweaks = useRef(null);
  if (initialTweaks.current === null) {
    initialTweaks.current = { ...TWEAK_DEFAULTS, ...(readTweakPrefs('_last') || {}) };
  }
  const [tweaks, setTweak] = useTweaks(initialTweaks.current);
  // Keep the latest tweaks reachable from the persisting setter without making
  // it a dependency (so the callback isn't re-created on every change).
  const tweaksRef = useRef(tweaks);
  tweaksRef.current = tweaks;
  // ⚠ Initial context picks up the magic link if it was just read from ?t=
  const initialCtx = getCurrentContext() === 'portal' ? 'portal' : 'internal';
  const [ctx, setCtx] = useStateApp(initialCtx);
  // A KYC-submission link (&kyc=1) lands the supplier straight on Documents.
  const [screen, setScreen] = useStateApp(
    initialCtx === 'portal' ? (shouldLandOnKyc() ? 'p-docs' : 'p-dashboard') : 'database'
  );
  useEffectApp(() => { if (shouldLandOnKyc()) clearKycLanding(); }, []);
  const [openSupplier, setOpenSupplier] = useStateApp(null);
  const [showForgot, setShowForgot] = useStateApp(false);
  // ⚠ Set/reset-password link (?pw=<token>) consumed once on first render.
  const [pwToken, setPwToken] = useStateApp(() => readPwLinkFromUrl());
  const [modal, setModal] = useStateApp(null); // 'invite' | 'shortcuts' | 'payrun'
  const [menu, setMenu] = useStateApp({ type: null, anchor: null });

  // ⚠ Live-backend state (added on top of the prototype)
  const [staffToken, setStaffToken]   = useStateApp(getCurrentToken());
  const [staffProfile, setStaffProfile] = useStateApp(getStaffProfile());
  const [vendors, setVendors]         = useStateApp(null);
  const [approvals, setApprovals]     = useStateApp([]);
  const [invoices, setInvoices]       = useStateApp([]);
  const [audits, setAudits]           = useStateApp([]);          // Phase 3
  const [ratings, setRatings]         = useStateApp([]);          // Phase 3
  const [compliance, setCompliance]   = useStateApp([]);          // Phase 3
  const [staffUsers, setStaffUsers]   = useStateApp([]);          // Phase 6: PM dropdown source
  const [portalVendor, setPortalVendor] = useStateApp(null);   // Phase 2: current vendor in portal context
  const [portalConfirmed, setPortalConfirmed] = useStateApp(isPortalSessionConfirmed());

  // Phase 3 polish — toasts + rating-on-approve modal
  const [toasts, setToasts] = useStateApp([]);
  const [ratingModal, setRatingModal] = useStateApp({ open: false, invoice: null });
  const [editVendor, setEditVendor] = useStateApp(null);   // Phase 4 polish — Edit profile modal
  // Archive / delete supplier confirmation — { kind: 'archive'|'unarchive'|'delete', vendor }
  const [confirmVendor, setConfirmVendor] = useStateApp(null);
  const [confirmBusy, setConfirmBusy]     = useStateApp(false);
  // Delete-invoice confirmation (admin only) — holds the invoice being deleted.
  const [confirmInvoice, setConfirmInvoice] = useStateApp(null);
  const [confirmInvoiceBusy, setConfirmInvoiceBusy] = useStateApp(false);
  const showToast = (message, kind = 'success') => {
    const id = Math.random().toString(36).slice(2);
    setToasts(t => [...t, { id, message, kind }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  };
  // Expose globally so deep-nested screens / api errors can ping the user
  useEffectApp(() => { window.__toast = showToast; return () => { delete window.__toast; }; }, []);
  // Drag-to-resize columns on every table (delegated; remembers widths). One-time.
  useEffectApp(() => installColumnResize(), []);
  const [vendorsErr, setVendorsErr]   = useStateApp(null);
  // A refresh can name the sections it actually needs. An invoice transition
  // only changes invoices and the audit log, and re-pulling all seven tables
  // (~290 KB) behind every approval is what makes a run of quick approvals
  // start contending with itself. null = everything.
  const [refreshReq, setRefreshReq] = useStateApp({ n: 0, scope: null });
  const refreshTick = refreshReq.n;
  // `refresh` is also handed straight to onClick handlers, so ignore anything
  // that isn't an explicit section list.
  const refresh = (scope) => setRefreshReq(r => ({
    n: r.n + 1,
    scope: Array.isArray(scope) && scope.length ? scope : null,
  }));

  useEffectApp(() => onSignIn(() => {
    setStaffToken(getCurrentToken());
    setStaffProfile(getStaffProfile());
  }), []);

  // Fetch whoami after sign-in to grab the role from the Users sheet.
  // We only take `role` + `context` — the display name stays whatever Google
  // has on file, so it doesn't flicker on every reload.
  // The internal app gets its role from api_bootstrap (below) in the same round
  // trip as its data — calling whoami separately here would put a second GAS
  // round trip (~2s each) in front of every load.
  useEffectApp(() => {
    if (!staffToken || ctx === 'internal') return;
    let cancelled = false;
    api.whoami().then(me => {
      if (cancelled || !me) return;
      setStaffProfile(prev => ({ ...(prev || {}), role: me.role, context: me.context }));
      setVendorsErr(null);
    }).catch(err => {
      if (cancelled) return;
      setVendorsErr(`whoami: ${err.message}`);
    });
    return () => { cancelled = true; };
  }, [staffToken, ctx]);

  // Fetch internal data when staff signed in — ONE backend call for every table
  // plus whoami. This used to be seven parallel calls fired after a whoami round
  // trip; each GAS call costs ~2s of transport before its handler runs, and the
  // overlapping heavy reads are what made random endpoints come back empty.
  //
  // A section that failed arrives as { __error } instead of rows. We keep the
  // previous value for those — replacing good data with [] on a hiccup is what
  // made the billing table look wrong until a manual refresh.
  useEffectApp(() => {
    if (!staffToken || ctx !== 'internal') return;
    let cancelled = false;
    api.bootstrap(refreshReq.scope).then(boot => {
      if (cancelled || !boot) return;
      const errs = [];
      // A section is rows on success, { __error } on failure. `keep` leaves the
      // current state untouched in the failure case.
      const apply = (name, value, setter) => {
        if (Array.isArray(value)) { setter(value); return; }
        if (value && value.__error) errs.push(`${name}: ${value.__error}`);
      };
      if (boot.whoami) {
        setStaffProfile(prev => ({ ...(prev || {}), role: boot.whoami.role, context: boot.whoami.context }));
      }
      // A scoped refresh returns only the sections it asked for; the rest come
      // back undefined and `apply` leaves their current state alone.
      apply('vendors',    boot.vendors,    setVendors);
      apply('approvals',  boot.approvals,  setApprovals);
      apply('invoices',   boot.invoices,   setInvoices);
      apply('ratings',    boot.ratings,    setRatings);
      apply('compliance', boot.compliance, setCompliance);
      apply('users',      boot.users,      setStaffUsers);
      apply('audit',      boot.audit,      setAudits);
      // A viewer legitimately can't list approvals — that's not worth a banner.
      const shown = errs.filter(e => !/^approvals: role required/.test(e));
      setVendorsErr(shown.length ? shown.join(' · ') : null);
    }).catch(err => {
      if (cancelled) return;
      setVendorsErr(err.message);   // state left as-is; nothing is blanked
    });
    return () => { cancelled = true; };
  }, [staffToken, ctx, refreshReq]);

  // The Audit screen is the only place that renders the before/after diffs, and
  // those diffs are most of the audit payload (~300 KB for 200 rows). The
  // bootstrap pulls a slim copy for the notification bell and the supplier
  // activity list; the full version is fetched only when the screen is opened.
  const [auditFull, setAuditFull] = useStateApp(false);
  useEffectApp(() => {
    if (screen !== 'audit' || !staffToken || ctx !== 'internal' || auditFull) return;
    let cancelled = false;
    api.listAudit({ limit: 200 }).then(rows => {
      if (cancelled || !Array.isArray(rows)) return;
      setAudits(rows);
      setAuditFull(true);
    }).catch(() => { /* slim rows are already on screen — the diffs just stay empty */ });
    return () => { cancelled = true; };
  }, [screen, staffToken, ctx, auditFull]);
  // A refresh replaces the audit rows with slim ones again.
  useEffectApp(() => { setAuditFull(false); }, [refreshTick]);

  // ⚠ Phase 2 — Fetch portal data (vendor profile + own invoices).
  // One round trip since 2026-09-16 (api_portalBootstrap) instead of two
  // parallel ones. A failed section keeps its previous value.
  useEffectApp(() => {
    if (ctx !== 'portal' || getCurrentContext() !== 'portal') return;
    let cancelled = false;
    api.portalBootstrap(refreshReq.scope).then(boot => {
      if (cancelled || !boot) return;
      if (boot.vendor && !boot.vendor.__error) setPortalVendor(boot.vendor);
      else if (boot.vendor && boot.vendor.__error) console.warn('portal vendor', boot.vendor.__error);
      if (Array.isArray(boot.invoices)) setInvoices(boot.invoices);
      else if (boot.invoices && boot.invoices.__error) console.warn('portal invoices', boot.invoices.__error);
      setVendorsErr(null);
    }).catch(err => {
      if (cancelled) return;
      console.warn('portalBootstrap', err);
      setVendorsErr(err.message);
    });
    return () => { cancelled = true; };
  }, [ctx, refreshReq]);

  // "Waking up the server" strip — on while any backend call has been pending
  // for more than a few seconds (cold Apps Script instance ≈ 20–40 s).
  const [slowCall, setSlowCall] = useStateApp(false);
  useEffectApp(() => subscribeSlow(setSlowCall), []);

  const handleDecide = async (id, decision, reason) => {
    try {
      await api.decideApproval(id, decision, reason);
      showToast(`Vendor approval ${decision}`, 'success');
      refresh();
    } catch (e) { showToast(`Failed: ${e.message}`, 'error'); throw e; }
  };
  const handleRegister = async (form) => {
    try {
      const result = await api.registerVendor(form);
      showToast(`Supplier "${form.name}" created`, 'success');
      refresh();
      // Offer to send the supplier a KYC-submission link so they upload their
      // own documents (company info stays with the team).
      const send = window.confirm(
        `Supplier "${form.name}" created.\n\n` +
        `Generate a KYC submission link and email it to ${form.email} so they can upload their documents?`
      );
      if (send) await handleIssueKycLink(result.vendor_id, form.name);
    } catch (e) { showToast(`Failed: ${e.message}`, 'error'); throw e; }
  };
  const handleSubmitInvoice = async (form) => {
    try {
      const result = await api.submitInvoice(form);
      showToast(`Invoice ${result.invoice_id} submitted`, 'success');
      refresh();
      setScreen('p-billing');
    } catch (e) { showToast(`Failed: ${e.message}`, 'error'); throw e; }
  };
  /**
   * Swap one invoice in local state for the row the backend just wrote.
   *
   * The billing table used to only move an invoice once a full re-fetch came
   * back, so the approver watched a spinner for the whole round trip — and if
   * that re-fetch hiccuped, the row never moved at all. api_setInvoiceStatus
   * returns the updated row, so the correct new state is already in hand.
   */
  const patchInvoice = (id, updated) => {
    if (!updated) return;
    setInvoices(prev => (Array.isArray(prev)
      ? prev.map(b => ((b.invoice_id || b.id) === id ? { ...b, ...updated } : b))
      : prev));
  };

  // Phase 3 — invoice status workflow
  const handleStatusChange = async (id, status, note, opts = {}) => {
    try {
      const res = await api.setInvoiceStatus(id, status, note, opts.company, opts.category, opts.scheduled_date);
      patchInvoice(id, res?.invoice);           // row moves now, not in 30s
      const extra = status === 'approved-payment' && opts.company
        ? ` · ${opts.company === 'brandstorm' ? 'Brandstorm' : 'MotionReach'} AP`
        : '';
      showToast(`Invoice ${id} → ${STATUS_LABEL[status] || status}${extra}`, 'success');
      refresh(['invoices', 'audit']);            // reconcile in the background
    } catch (e) {
      showToast(`Status update failed: ${e.message}`, 'error');
      throw e;
    }
  };

  // PM approve has a mandatory rating step. ScreenBillingInbox calls this
  // instead of handleStatusChange when current status is pm-review.
  const handlePmApprove = (invoice) => setRatingModal({ open: true, invoice });

  // Supplier archive / delete — open a confirmation, then run on confirm.
  const handleArchiveVendor = (vendor, unarchive) =>
    setConfirmVendor({ kind: unarchive ? 'unarchive' : 'archive', vendor });
  const handleDeleteVendor = (vendor) => setConfirmVendor({ kind: 'delete', vendor });
  const runConfirmVendor = async () => {
    if (!confirmVendor) return;
    const { kind, vendor } = confirmVendor;
    const vid = vendor.vendor_id || vendor.id;
    setConfirmBusy(true);
    try {
      if (kind === 'delete') {
        await api.deleteVendor(vid);
        showToast(`Deleted ${vendor.name}`, 'success');
        setOpenSupplier(null);
      } else if (kind === 'archive') {
        await api.archiveVendor(vid);
        showToast(`Archived ${vendor.name}`, 'success');
        setOpenSupplier(null);
      } else {
        await api.archiveVendor(vid, { unarchive: true });
        showToast(`Restored ${vendor.name}`, 'success');
        setOpenSupplier(prev => (prev ? { ...prev, status: 'active' } : prev));
      }
      setConfirmVendor(null);
      refresh();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setConfirmBusy(false);
    }
  };

  // Delete an invoice (admin only) — open a confirmation, then run on confirm.
  const handleDeleteInvoice = (invoice) => setConfirmInvoice(invoice);
  const runConfirmInvoice = async () => {
    if (!confirmInvoice) return;
    const id = confirmInvoice.invoice_id || confirmInvoice.id;
    setConfirmInvoiceBusy(true);
    try {
      await api.deleteInvoice(id);
      showToast(`Invoice ${id} deleted`, 'success');
      setConfirmInvoice(null);
      refresh();
    } catch (e) {
      showToast(`Delete failed: ${e.message}`, 'error');
    } finally {
      setConfirmInvoiceBusy(false);
    }
  };

  // RatingApproveModal calls this on confirm — submits rating then advances.
  const handleConfirmRatingApprove = async (rating, note) => {
    const inv = ratingModal.invoice;
    if (!inv) return;
    try {
      await api.submitRating({
        vendor_id: inv.vendor_id || inv.vid,
        project: inv.project || '—',
        quality_5: rating.quality,
        timeliness_5: rating.timeliness,
        comms_5: rating.comms,
        value_5: rating.value,
        comment: rating.comment || '',
        engagement_ref: inv.invoice_id || inv.id,
      });
      const invId = inv.invoice_id || inv.id;
      const res = await api.setInvoiceStatus(invId, 'finance-verify', note);
      patchInvoice(invId, res?.invoice);
      showToast(`Approved & rated ${inv.vendor || inv.vendor_name || inv.vendor_id}`, 'success');
      setRatingModal({ open: false, invoice: null });
      refresh(['invoices', 'ratings', 'vendors', 'audit']);   // rating updates the vendor score
    } catch (e) {
      showToast(`Failed: ${e.message}`, 'error');
      throw e;
    }
  };
  const handleSetCompliance = async (vendor_id, compliance) => {
    try {
      await api.setVendorCompliance(vendor_id, compliance);
      const msg = compliance === 'verified' ? 'KYC verified' : compliance === 'rejected' ? 'KYC flagged' : 'KYC reset to pending';
      showToast(msg, 'success');
      if (openSupplier && (openSupplier.vendor_id || openSupplier.id) === vendor_id) {
        setOpenSupplier({ ...openSupplier, compliance });
      }
      refresh();
    } catch (e) { showToast(`Failed: ${e.message}`, 'error'); }
  };
  const handleIssueMagicLink = async (vendor_id) => {
    const result = await api.issueMagicLink(vendor_id);
    try {
      await navigator.clipboard.writeText(result.url);
      alert(`Magic link copied to clipboard.\n\nValid ${result.expires_in_days} days for ${result.vendor_name}.\nSend this URL to the vendor — they'll be auto-signed in to their portal.`);
    } catch {
      prompt(`Magic link for ${result.vendor_name} (valid ${result.expires_in_days} days):`, result.url);
    }
  };
  // KYC-submission link — copies the URL AND emails the supplier (Guy's choice).
  // Reused from the register flow and the supplier-detail "Send KYC link" button.
  const handleIssueKycLink = async (vendor_id, vendorName) => {
    try {
      const result = await api.issueKycLink(vendor_id);
      let copied = false;
      try { await navigator.clipboard.writeText(result.url); copied = true; } catch {}
      const who = result.vendor_name || vendorName || vendor_id;
      alert(
        `KYC submission link for ${who}${copied ? ' copied to clipboard.' : ':'}` +
        `${result.emailed ? '\nEmailed to the supplier.' : ''}\n` +
        `Valid ${result.expires_in_days} days. The supplier lands on the document-upload screen.\n\n${result.url}`
      );
      showToast(`KYC link ready${result.emailed ? ' & emailed to supplier' : ''}`, 'success');
    } catch (e) {
      showToast(`Failed to issue KYC link: ${e.message}`, 'error');
    }
  };

  const bellRef = React.useRef(null);
  const gearTopRef = React.useRef(null);
  const gearSideRef = React.useRef(null);

  const openMenu = (type, anchor) => setMenu({ type, anchor });
  const closeMenu = () => setMenu({ type: null, anchor: null });

  // Expose modal opener + nav globally so deep-nested screens can trigger them
  React.useEffect(() => {
    window.__app = {
      openModal: setModal,
      gotoScreen: (s) => { setScreen(s); setOpenSupplier(null); },
    };
    return () => { delete window.__app; };
  }, []);

  // Global keyboard shortcuts
  React.useEffect(() => {
    const onKey = (e) => {
      // ⌘K / Ctrl+K always opens the command palette, even from inside inputs
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setModal((m) => m === "cmdk" ? null : "cmdk");
        return;
      }
      if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable)) return;
      if (e.key === "?" || (e.key === "/" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        setModal("shortcuts");
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // ── Per-user appearance persistence (see TWEAK_PREFS_NS) ────────────────────
  const tweakUserKeyStr = tweakUserKey(staffProfile, portalVendor);
  const persistKeyRef = useRef(tweakUserKeyStr);
  persistKeyRef.current = tweakUserKeyStr;

  // User-facing setter: update in-memory prefs AND persist them immediately —
  // to the device `_last` fallback and (once known) the signed-in user's own
  // key. Use this everywhere a person changes theme/density/accent.
  const setTweakPersist = React.useCallback((keyOrEdits, val) => {
    const edits = (typeof keyOrEdits === 'object' && keyOrEdits !== null)
      ? keyOrEdits : { [keyOrEdits]: val };
    const next = { ...tweaksRef.current, ...edits };
    setTweak(edits);
    writeTweakPrefs('_last', next);
    if (persistKeyRef.current) writeTweakPrefs(persistKeyRef.current, next);
  }, [setTweak]);

  // Load the signed-in user's saved appearance once, when their identity becomes
  // known (or changes to a different user). This only reads + applies — it never
  // writes, so it can't clobber the stored prefs it just loaded.
  const hydratedForRef = useRef(null);
  useEffectApp(() => {
    if (!tweakUserKeyStr) { hydratedForRef.current = null; return; }
    if (hydratedForRef.current === tweakUserKeyStr) return;
    hydratedForRef.current = tweakUserKeyStr;
    const saved = readTweakPrefs(tweakUserKeyStr);
    if (saved) { setTweak(saved); writeTweakPrefs('_last', saved); }
  }, [tweakUserKeyStr]);

  // Apply theme/density/accent at root
  useEffectApp(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", tweaks.theme);
    root.setAttribute("data-density", tweaks.density);
    const a = ACCENTS[tweaks.accent] || ACCENTS["#9437FF"];
    root.style.setProperty("--accent", tweaks.theme === "light" ? a.light : a.dark);
    root.style.setProperty("--accent-text", a.text);
  }, [tweaks.theme, tweaks.density, tweaks.accent]);

  const isPortal = ctx === "portal";
  const hasPortalToken = getCurrentContext() === 'portal';

  // ⚠ Set / reset password — reached via the ?pw=<token> email link. Takes
  // priority over everything else so a logged-in session can't shadow it.
  if (pwToken) {
    return <SetPasswordScreen token={pwToken} onCancel={() => setPwToken(null)}/>;
  }

  // ⚠ Unified sign-in surface (Claude Design login handover, 2026-05-31).
  // Shown whenever nobody is authenticated. Internal = real Google SSO;
  // Supplier = real email + password (magic-link sign-in still works too).
  // The supplier "Forgot?" link opens the password-reset request flow.
  if (!staffToken && !hasPortalToken) {
    if (showForgot) return <ForgotScreen onBack={() => setShowForgot(false)}/>;
    return (
      <LoginScreen
        initialMode={isPortal ? 'supplier' : 'internal'}
        onForgot={() => setShowForgot(true)}
      />
    );
  }

  // ⚠ Portal welcome — vendor arrived with a magic link, confirms entry.
  if (isPortal && hasPortalToken && !portalConfirmed) {
    return <PortalWelcomeScreen
      vendor={portalVendor}
      onContinue={() => { confirmPortalSession(); setPortalConfirmed(true); }}
      onSignOut={signOut}
    />;
  }


  // Recompute nav counts from live data so the badges are never mock/stale.
  // While loading (vendors === null) show no badge instead of the mock count.
  const liveInternalNav = INTERNAL_NAV.map(n => {
    if (n.id === 'database') return { ...n, count: vendors ? vendors.length : undefined };
    if (n.id === 'approval') return { ...n, count: approvals.length };
    // Billing badge = invoices awaiting PM review (an action count), not the
    // total inbox. Hidden entirely when nothing is pending.
    if (n.id === 'billing') {
      const pmPending = invoices.filter(i => i.status === 'pm-review').length;
      return { ...n, count: pmPending || undefined };
    }
    return n;
  });
  // Portal "My billing" badge must reflect the vendor's real invoice count,
  // not the MY_BILLS mock baked into PORTAL_NAV.
  const livePortalNav = PORTAL_NAV.map(n =>
    n.id === 'p-billing' ? { ...n, count: invoices.length } : n
  );
  const sidebarNav = isPortal ? livePortalNav : liveInternalNav;
  const sidebarNav2 = isPortal ? PORTAL_NAV_2 : INTERNAL_NAV_2;

  const onPickScreen = (id) => {
    setOpenSupplier(null);
    setScreen(id);
  };

  // Render current screen
  let body;
  if (!isPortal) {
    if (openSupplier) {
      body = <ScreenDetail
        supplier={openSupplier}
        onBack={() => setOpenSupplier(null)}
        onIssueMagicLink={handleIssueMagicLink}
        canIssueMagicLink={['admin','pm'].includes(staffProfile?.role)}
        onIssueKycLink={handleIssueKycLink}
        canIssueKycLink={['admin','pm'].includes(staffProfile?.role)}
        onEdit={(v) => setEditVendor(v)}
        canEdit={['admin','pm'].includes(staffProfile?.role)}
        onArchive={handleArchiveVendor}
        onDelete={handleDeleteVendor}
        canArchive={staffProfile?.role === 'admin'}
        invoices={invoices}
        ratings={ratings}
        audits={audits}
        users={staffUsers}
        compliance={compliance}
      />;
    } else if (screen === "database") {
      body = <ScreenDatabase
        vendors={vendors}
        invoices={invoices}
        onOpenSupplier={s => setOpenSupplier(s)}
        canCreate={['admin','pm'].includes(staffProfile?.role)}
        onNewSupplier={() => setScreen("register")}/>;
    } else if (screen === "register") {
      // draftKey scopes the local draft per staff member so two people on one
      // machine don't share a half-filled supplier form.
      body = <ScreenRegister onCancel={() => setScreen("database")} onSubmit={handleRegister}
        draftKey={'register:' + (staffProfile?.email || 'staff')}/>;
    } else if (screen === "approval") {
      body = <ScreenApproval approvals={approvals} vendors={vendors || []} compliance={compliance} onDecide={handleDecide}/>;
    } else if (screen === "quality") {
      body = <ScreenQuality ratings={ratings} vendors={vendors || []} onNewReview={() => setModal('new-review')}/>;
    } else if (screen === "audit") {
      body = <ScreenAudit audits={audits}/>;
    } else if (screen === "reports") {
      body = <ScreenReports vendors={vendors || []} invoices={invoices}/>;
    } else if (screen === "billing") {
      body = <ScreenBillingInbox invoices={invoices} role={staffProfile?.role} staffEmail={staffProfile?.email} onStatusChange={handleStatusChange} onPmApprove={handlePmApprove} onRefresh={refresh}
        onDelete={handleDeleteInvoice} canDelete={staffProfile?.role === 'admin'}/>;
    } else {
      body = <PlaceholderScreen label={SCREEN_TITLE[screen] || screen}/>;
    }
  } else {
    if (screen === "p-submit") {
      body = <ScreenSubmit
        onCancel={() => setScreen("p-billing")}
        onSubmit={handleSubmitInvoice}
        vendor={portalVendor}
        // Scope the draft to this vendor; null until the profile loads so we
        // don't start a draft under a placeholder key.
        draftKey={portalVendor?.vendor_id ? 'invoice:' + portalVendor.vendor_id : null}/>;
    } else if (screen === "p-billing") {
      body = <ScreenPortalBilling onSubmit={() => setScreen("p-submit")} invoices={invoices} onRefresh={refresh}/>;
    } else if (screen === "p-dashboard") {
      body = <PortalDashboard
        onSubmit={() => setScreen("p-submit")}
        onBilling={() => setScreen("p-billing")}
        vendor={portalVendor}
        invoices={invoices}/>;
    } else if (screen === "p-profile") {
      body = <ScreenCompanyProfile vendor={portalVendor} invoices={invoices}/>;
    } else if (screen === "p-docs") {
      body = <ScreenDocuments invoices={invoices} vendor={portalVendor} onProfileSaved={refresh}/>;
    } else if (screen === "p-settings") {
      body = <ScreenSettings vendor={portalVendor}/>;
    } else {
      body = <PlaceholderScreen label={SCREEN_TITLE[screen] || screen}/>;
    }
  }

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand-lockup">
          <img src={tweaks.theme === "light" ? "assets/logo-color.png" : "assets/logo-white.png"} alt="MotionReach"/>
          <div className="divider-v"></div>
          <img className="brandstorm-mark"
            src={tweaks.theme === "light" ? "assets/brandstorm-color.png" : "assets/brandstorm-white.png"}
            alt="Brandstorm"/>
        </div>
        <div className="brand-sublabel">Vendor OS · v0.4</div>

        {/* ⚠ MODIFIED FROM PORT — switcher only visible to staff *who don't
            also hold a vendor token*. A magic-link session is exclusively
            vendor; staff token gets cleared on link consumption. */}
        {staffToken && !hasVendorToken() && (
          <div className="ctx-switcher">
            <button className={ctx === "internal" ? "active" : ""}
              onClick={() => { setCtx("internal"); setScreen("database"); setOpenSupplier(null); }}>
              {t("Internal")}
            </button>
            <button className={ctx === "portal" ? "active" : ""}
              onClick={() => { setCtx("portal"); setScreen("p-dashboard"); }}>
              {t("Supplier")}
            </button>
          </div>
        )}

        <div className="nav-section">
          {sidebarNav.map(n => (
            <NavItem key={n.id} item={n}
              active={screen === n.id || (n.id === "database" && openSupplier)}
              onClick={() => onPickScreen(n.id)}/>
          ))}
        </div>

        <div className="nav-section">
          <div className="nav-section-title">{isPortal ? t("Account") : t("Tools")}</div>
          {sidebarNav2.map(n => (
            <NavItem key={n.id} item={n}
              active={screen === n.id}
              onClick={() => onPickScreen(n.id)}/>
          ))}
        </div>

        {isPortal && (
          <div className="nav-section">
            <button className="btn btn-ghost btn-sm" style={{justifyContent:"flex-start", padding: "7px 10px"}}
              onClick={signOut}>
              <IconLogout size={14}/> {t("Sign out")}
            </button>
          </div>
        )}

        <div className="sidebar-footer">
          {isPortal ? (
            <>
              <Avatar name={portalVendor?.name || "Vendor"} size="sm"/>
              <div className="user-meta">
                <span className="name">{portalVendor?.name || "Vendor"}</span>
                <span className="role">{portalVendor?.vendor_id ? `Vendor · ${portalVendor.vendor_id}` : "Vendor portal"}</span>
              </div>
            </>
          ) : (
            <>
              {/* ⚠ MODIFIED FROM PORT — live user from staffProfile / whoami */}
              <Avatar name={staffProfile?.name || "Staff"} size="sm"/>
              <div className="user-meta">
                <span className="name">{staffProfile?.name || t("Staff member")}</span>
                <span className="role">
                  {staffProfile?.role
                    ? t(staffProfile.role.charAt(0).toUpperCase() + staffProfile.role.slice(1))
                    : "MotionReach"}
                </span>
              </div>
            </>
          )}
          <button ref={gearSideRef} className="icon-btn" title="Settings"
            onClick={() => menu.type === "settings-side" ? closeMenu() : openMenu("settings-side", gearSideRef.current)}>
            <IconSettings size={14}/>
          </button>
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <div className="breadcrumb">
            {isPortal
              ? <><span>{t("Supplier portal")}</span><span className="sep">/</span><span className="cur">{t(SCREEN_TITLE[screen] || screen)}</span></>
              : openSupplier
                ? <><span style={{cursor:"pointer"}} onClick={() => setOpenSupplier(null)}>{t("Suppliers")}</span><span className="sep">/</span><span className="cur">{openSupplier.name}</span></>
                : <><span>{t("Procurement")}</span><span className="sep">/</span><span className="cur">{t(SCREEN_TITLE[screen] || screen)}</span></>
            }
          </div>

          <div className="topbar-spacer"></div>

          <div className="topbar-search" onClick={() => setModal('cmdk')} style={{cursor: "pointer"}}>
            <IconSearch size={14} className="icon"/>
            <input readOnly placeholder={isPortal ? t("Search invoices, projects…") : t("Search suppliers, invoices, contacts…")}
              style={{cursor: "pointer"}} onClick={(e) => { e.preventDefault(); setModal('cmdk'); }}/>
            <span className="kbd" style={{position:"absolute", right: 8, top: "50%", transform:"translateY(-50%)"}}>⌘K</span>
          </div>

          <div className="topbar-actions">
            <div title={t("Language")}
              style={{display:"flex", border:"1px solid var(--border-subtle)", borderRadius: 8, overflow:"hidden", marginRight: 4}}>
              {["en","th"].map(l => (
                <button key={l} onClick={() => setLang(l)}
                  style={{
                    border: "none", cursor: "pointer", width: "auto", padding: "0 9px", height: 30,
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                    background: lang === l ? "var(--accent-soft)" : "transparent",
                    color: lang === l ? "var(--accent)" : "var(--text-tertiary)",
                  }}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button ref={bellRef} className="icon-btn"
              onClick={() => menu.type === "notifs" ? closeMenu() : openMenu("notifs", bellRef.current)}
              style={{position: "relative"}}>
              <IconBell size={16}/>
              <span style={{
                position: "absolute", top: 4, right: 4,
                width: 8, height: 8, borderRadius: "50%",
                background: "var(--danger)",
                border: "2px solid var(--bg-primary)",
              }}></span>
            </button>
            <button ref={gearTopRef} className="icon-btn"
              onClick={() => menu.type === "settings-top" ? closeMenu() : openMenu("settings-top", gearTopRef.current)}>
              <IconSettings size={16}/>
            </button>
          </div>
        </div>

        {vendorsErr && (
          <div style={{
            margin: "0 24px 8px", padding: "10px 14px",
            background: "color-mix(in oklab, var(--danger) 12%, transparent)",
            border: "1px solid var(--danger)", borderRadius: 8,
            color: "var(--danger)", fontSize: 13, lineHeight: 1.5,
          }}>
            <strong>{t('Backend error.')}</strong> {vendorsErr}
            <div style={{fontSize: 11, marginTop: 4, opacity: 0.8}}>
              Common causes: your email isn't in the Users tab (or <code>active</code> is FALSE),
              or your role isn't admin/pm/finance/viewer. Check the spreadsheet.
            </div>
          </div>
        )}
        {body}
      </main>

      <Popover open={menu.type === "notifs"} anchor={menu.anchor} onClose={closeMenu} width={400}>
        <NotificationsMenu onClose={closeMenu} audits={audits}/>
      </Popover>
      <Popover open={menu.type === "settings-top"} anchor={menu.anchor} onClose={closeMenu} width={300}>
        <SettingsMenu theme={tweaks.theme} density={tweaks.density} accent={tweaks.accent}
          onTheme={v => setTweakPersist("theme", v)}
          onDensity={v => setTweakPersist("density", v)}
          onAccent={v => setTweakPersist("accent", v)}
          isPortal={isPortal}
          user={isPortal
            ? (portalVendor ? { name: portalVendor.name, email: portalVendor.email, role: `Vendor · ${portalVendor.vendor_id}` } : null)
            : staffProfile}
          onSignOut={signOut}
          onClose={closeMenu}/>
      </Popover>
      <Popover open={menu.type === "settings-side"} anchor={menu.anchor} onClose={closeMenu} align="bottom-left" width={300}>
        <SettingsMenu theme={tweaks.theme} density={tweaks.density} accent={tweaks.accent}
          onTheme={v => setTweakPersist("theme", v)}
          onDensity={v => setTweakPersist("density", v)}
          onAccent={v => setTweakPersist("accent", v)}
          isPortal={isPortal}
          user={isPortal
            ? (portalVendor ? { name: portalVendor.name, email: portalVendor.email, role: `Vendor · ${portalVendor.vendor_id}` } : null)
            : staffProfile}
          onSignOut={signOut}
          onClose={closeMenu}/>
      </Popover>

      <InviteModal    open={modal === "invite"}    onClose={() => setModal(null)} onAdded={refresh}/>
      <ShortcutsModal open={modal === "shortcuts"} onClose={() => setModal(null)}/>
      <PayRunModal    open={modal === "payrun"}    onClose={() => setModal(null)} invoices={invoices} onScheduled={refresh}/>

      <CommandPalette
        open={modal === "cmdk"}
        onClose={() => setModal(null)}
        vendors={vendors || []}
        invoices={invoices}
        onPickVendor={(v) => { setOpenSupplier(v); setScreen("database"); setCtx("internal"); setModal(null); }}
        onPickInvoice={(inv) => { setScreen("billing"); setCtx("internal"); setModal(null); }}
        onPickScreen={(id) => { setScreen(id); setOpenSupplier(null); setCtx(id.startsWith("p-") ? "portal" : "internal"); setModal(null); }}
      />

      <RatingApproveModal
        open={ratingModal.open}
        invoice={ratingModal.invoice}
        onClose={() => setRatingModal({ open: false, invoice: null })}
        onConfirm={handleConfirmRatingApprove}
      />

      <NewReviewModal
        open={modal === "new-review"}
        vendors={vendors || []}
        onClose={() => setModal(null)}
        onSubmitted={() => { setModal(null); refresh(); }}
      />

      <EditVendorModal
        open={!!editVendor}
        vendor={editVendor}
        isAdmin={staffProfile?.role === 'admin'}
        pmUsers={(staffUsers || []).filter(u => String(u.role || '').toLowerCase() === 'pm')}
        onClose={() => setEditVendor(null)}
        onSaved={(updated) => {
          setEditVendor(null);
          // Keep the open supplier in sync with the edits the user just made
          if (openSupplier && (openSupplier.vendor_id || openSupplier.id) === (updated.vendor_id || updated.id)) {
            setOpenSupplier({ ...openSupplier, ...updated });
          }
          refresh();
        }}
      />

      <ConfirmModal
        open={!!confirmVendor}
        busy={confirmBusy}
        danger={confirmVendor?.kind === 'delete'}
        title={confirmVendor?.kind === 'delete' ? t('Delete supplier?')
          : confirmVendor?.kind === 'unarchive' ? t('Restore supplier?')
          : t('Archive supplier?')}
        subtitle={confirmVendor ? `${confirmVendor.vendor.vendor_id || confirmVendor.vendor.id} · ${confirmVendor.vendor.name || ''}` : ''}
        confirmLabel={confirmVendor?.kind === 'delete' ? t('Delete permanently')
          : confirmVendor?.kind === 'unarchive' ? t('Restore')
          : t('Archive')}
        body={confirmVendor?.kind === 'delete'
          ? t('This permanently removes the supplier and its logins, tokens and KYC documents. It cannot be undone. (Suppliers with invoices cannot be deleted — archive them instead.)')
          : confirmVendor?.kind === 'unarchive'
            ? t('This restores the supplier to the active Suppliers list.')
            : t('This hides the supplier from the default list. Nothing is deleted — you can restore it anytime from the Archived filter.')}
        onConfirm={runConfirmVendor}
        onClose={() => setConfirmVendor(null)}
      />

      <ConfirmModal
        open={!!confirmInvoice}
        busy={confirmInvoiceBusy}
        danger
        title={t('Delete invoice?')}
        subtitle={confirmInvoice ? `${confirmInvoice.invoice_id || confirmInvoice.id} · ${confirmInvoice.vendor || confirmInvoice.vendor_name || ''}` : ''}
        confirmLabel={t('Delete invoice')}
        body={confirmInvoice && (confirmInvoice.ap_exported_at || ['approved-payment','scheduled','paid'].includes(confirmInvoice.status))
          ? t('This permanently removes the invoice, trashes its uploaded PDF and proof-of-performance files, AND removes its row from the Account Payable sheet. It cannot be undone. Only do this for a test or mistaken invoice.')
          : t('This permanently removes the invoice and trashes its uploaded PDF and proof-of-performance files. It cannot be undone.')}
        onConfirm={runConfirmInvoice}
        onClose={() => setConfirmInvoice(null)}
      />

      <ToastStack toasts={toasts} slow={slowCall} onDismiss={(id) => setToasts(t => t.filter(x => x.id !== id))}/>
      {SHOW_TWEAKS && (

      <TweaksPanel title="Tweaks">
        <TweakSection label="Appearance">
          <TweakRadio label="Theme"
            value={tweaks.theme}
            options={[{value:"dark",label:"Dark"},{value:"light",label:"Light"}]}
            onChange={v => setTweakPersist("theme", v)}/>
          <TweakRadio label="Density"
            value={tweaks.density}
            options={[{value:"compact",label:"Compact"},{value:"comfortable",label:"Comfy"}]}
            onChange={v => setTweakPersist("density", v)}/>
          <TweakColor label="Accent"
            value={tweaks.accent}
            options={Object.keys(ACCENTS)}
            onChange={v => setTweakPersist("accent", v)}/>
        </TweakSection>
        {/* ⚠ Staff-only demo buttons */}
        {staffToken && (
          <TweakSection label="Demo navigation">
            <div style={{display:"flex", flexDirection:"column", gap: 6}}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setCtx("internal"); setScreen("database"); setOpenSupplier(SUPPLIERS[0]); }}>
                Open supplier detail →
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setCtx("internal"); setScreen("register"); }}>
                Open registration flow →
              </button>
              <button className="btn btn-secondary btn-sm" onClick={signOut}>
                Sign out (clear tokens)
              </button>
            </div>
          </TweakSection>
        )}
        {!staffToken && (
          <TweakSection label="Session">
            <button className="btn btn-secondary btn-sm" style={{width: "100%"}} onClick={signOut}>
              Sign out
            </button>
          </TweakSection>
        )}
        {vendorsErr && (
          <TweakSection label="Backend error">
            <div style={{ fontSize: 11, color: "var(--danger)", lineHeight: 1.4 }}>
              {vendorsErr}
            </div>
          </TweakSection>
        )}
      </TweaksPanel>
      )}
    </div>
  );
}

/* ⚠ MODIFIED FROM PORT — toast stack pinned bottom-right.
 * window.__toast(msg, kind?) shows one; kind ∈ {'success', 'error', 'info'} */
function ToastStack({ toasts, onDismiss, slow }) {
  const tSlow = useT();
  if (!toasts.length && !slow) return null;
  return (
    <div style={{
      position: "fixed", right: 16, bottom: 16, zIndex: 9999,
      display: "flex", flexDirection: "column", gap: 8,
      pointerEvents: "none",
    }}>
      {slow && (
        <div style={{
          pointerEvents: "none", minWidth: 280, maxWidth: 380,
          padding: "10px 14px", background: "var(--bg-elev-2)",
          border: "1px solid var(--border-subtle)", borderLeft: "3px solid var(--warning)",
          borderRadius: 8, fontSize: 12, color: "var(--text-secondary)",
          boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
        }}>
          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{tSlow("Waking up the server…")}</div>
          <div>{tSlow("The first request after a quiet spell can take up to 30 seconds. Please don't re-click.")}</div>
        </div>
      )}
      {toasts.map(t => {
        const color = t.kind === 'error' ? 'var(--danger)' :
                      t.kind === 'info'  ? 'var(--info)'   :
                      'var(--success)';
        return (
          <div key={t.id}
            onClick={() => onDismiss(t.id)}
            style={{
              pointerEvents: "auto",
              minWidth: 280, maxWidth: 380,
              padding: "12px 16px",
              background: "var(--bg-elev-2)",
              border: `1px solid ${color}`,
              borderLeft: `4px solid ${color}`,
              borderRadius: 10,
              boxShadow: "var(--elevation-3)",
              fontSize: 13, color: "var(--text-primary)",
              cursor: "pointer",
              animation: "toast-in 200ms var(--easing-emphasized)",
            }}>
            <style>{`@keyframes toast-in { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform: none; } }`}</style>
            {t.message}
          </div>
        );
      })}
    </div>
  );
}

/* ⚠ MODIFIED FROM PORT — mandatory rating dialog shown when a PM approves
 * a `pm-review` invoice. Captures 4 scores + comment, then submits the
 * rating AND advances the invoice to finance-verify in one action. */
function RatingApproveModal({ open, invoice, onClose, onConfirm }) {
  const t = useT();
  const [scores, setScores] = useState({ quality: 5, timeliness: 5, comms: 5, value: 5 });
  const [comment, setComment] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setScores({ quality: 5, timeliness: 5, comms: 5, value: 5 }); setComment(""); setNote(""); }
  }, [open]);

  if (!open || !invoice) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm({ ...scores, comment }, note);
    } catch (e) { /* toast already shown */ }
    finally { setBusy(false); }
  };

  const Slider = ({ label, value, onChange }) => (
    <div className="field">
      <label className="field-label" style={{display: "flex", justifyContent: "space-between"}}>
        <span>{label}</span>
        <span className="mono" style={{color: "var(--accent)"}}>{value} / 5</span>
      </label>
      <input
        type="range" min="1" max="5" step="1"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: "var(--accent)" }}
      />
    </div>
  );

  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} width={580}>
      <ModalHeader
        title={t('Rate & approve')}
        subtitle={t('Required before PM approval · {name}', { name: invoice.vendor || invoice.vendor_name || invoice.vendor_id })}
        onClose={busy ? () => {} : onClose}
      />
      <div style={{padding: "18px 22px", overflowY: "auto"}}>
        <div style={{fontSize: 12, color: "var(--text-tertiary)", marginBottom: 14, lineHeight: 1.6}}>
          {t('Project: {project} · Invoice: {invoice}', { project: invoice.project || '—', invoice: invoice.invoice_id || invoice.id })}
        </div>
        <div className="form-grid">
          <Slider label={t('Quality of work')}  value={scores.quality}    onChange={v => setScores(s => ({...s, quality: v}))}/>
          <Slider label={t('Timeliness')}       value={scores.timeliness} onChange={v => setScores(s => ({...s, timeliness: v}))}/>
          <Slider label={t('Communication')}    value={scores.comms}      onChange={v => setScores(s => ({...s, comms: v}))}/>
          <Slider label={t('Value for money')}  value={scores.value}      onChange={v => setScores(s => ({...s, value: v}))}/>
          <div className="field full">
            <label className="field-label">{t('Comment for the supplier')} <span className="muted">({t('optional')})</span></label>
            <textarea className="input" rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder={t('What went well, what could improve…')}/>
          </div>
          <div className="field full">
            <label className="field-label">{t('Internal note for finance')} <span className="muted">({t('optional')})</span></label>
            <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={t('Any flag for the next reviewer…')}/>
          </div>
        </div>
      </div>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t('Cancel')}</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>
          {busy ? t('Submitting…') : t('Submit rating & approve')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ⚠ MODIFIED FROM PORT — ⌘K command palette. Search and navigate.
 * Searches across screens, vendors and invoices. Click a result to jump. */
function CommandPalette({ open, onClose, vendors, invoices, onPickVendor, onPickInvoice, onPickScreen }) {
  const t = useT();
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const SCREENS = [
    { id: "database",   label: "Suppliers",       hint: "Internal · directory" },
    { id: "approval",   label: "Approval queue",  hint: "Internal · pending" },
    { id: "billing",    label: "Billing inbox",   hint: "Internal · invoices" },
    { id: "quality",    label: "Quality reviews", hint: "Internal · ratings" },
    { id: "audit",      label: "Audit log",       hint: "Internal · history" },
    { id: "reports",    label: "Reports",         hint: "Internal · spend" },
    { id: "p-dashboard",label: "Portal dashboard",hint: "Vendor · home" },
    { id: "p-submit",   label: "Submit invoice",  hint: "Vendor · new" },
    { id: "p-billing",  label: "My billing",      hint: "Vendor · invoices" },
  ];

  const norm = q.trim().toLowerCase();
  const matchScreens  = norm
    ? SCREENS.filter(s => s.label.toLowerCase().includes(norm))
    : SCREENS.slice(0, 5);
  const matchVendors  = norm
    ? vendors.filter(v =>
        String(v.name||'').toLowerCase().includes(norm) ||
        String(v.vendor_id||v.id||'').toLowerCase().includes(norm)
      ).slice(0, 8)
    : [];
  const matchInvoices = norm
    ? invoices.filter(i =>
        String(i.invoice_id||i.id||'').toLowerCase().includes(norm) ||
        String(i.po_number||i.po||'').toLowerCase().includes(norm) ||
        String(i.project||'').toLowerCase().includes(norm) ||
        String(i.vendor||'').toLowerCase().includes(norm)
      ).slice(0, 8)
    : [];

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "var(--bg-overlay)",
      backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      zIndex: 1300, display: "flex", alignItems: "flex-start", justifyContent: "center",
      paddingTop: "12vh",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "100%", maxWidth: 620, maxHeight: "70vh",
        background: "var(--bg-secondary)",
        border: "1px solid var(--border-default)",
        borderRadius: 14,
        boxShadow: "var(--elevation-3)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        <div style={{padding: "14px 18px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", gap: 12}}>
          <span style={{fontSize: 14, color: "var(--text-tertiary)"}}>⌘K</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Search suppliers, invoices, screens…")}
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "var(--text-primary)", fontSize: 15, fontFamily: "var(--font-en)",
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              if (e.key === "Enter") {
                if (matchVendors[0]) onPickVendor(matchVendors[0]);
                else if (matchInvoices[0]) onPickInvoice(matchInvoices[0]);
                else if (matchScreens[0]) onPickScreen(matchScreens[0].id);
              }
            }}
          />
        </div>
        <div style={{overflowY: "auto", padding: "8px 0"}}>
          {matchScreens.length > 0 && (
            <Section label={t("Screens")}>
              {matchScreens.map(s => (
                <Row key={s.id} label={t(s.label)} hint={s.hint} onClick={() => onPickScreen(s.id)}/>
              ))}
            </Section>
          )}
          {matchVendors.length > 0 && (
            <Section label={t("Suppliers ({n})", { n: matchVendors.length })}>
              {matchVendors.map(v => (
                <Row key={v.vendor_id || v.id}
                  label={v.name}
                  hint={`${v.vendor_id || v.id} · ${v.cat || ''} · ${v.city || ''}`}
                  onClick={() => onPickVendor(v)}/>
              ))}
            </Section>
          )}
          {matchInvoices.length > 0 && (
            <Section label={t("Invoices ({n})", { n: matchInvoices.length })}>
              {matchInvoices.map(inv => (
                <Row key={inv.invoice_id || inv.id}
                  label={inv.invoice_id || inv.id}
                  hint={`${inv.vendor || ''} · ${inv.project || ''} · ${inv.status || ''}`}
                  onClick={() => onPickInvoice(inv)}/>
              ))}
            </Section>
          )}
          {q && !matchScreens.length && !matchVendors.length && !matchInvoices.length && (
            <div style={{padding: 24, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>
              {t("No results.")}
            </div>
          )}
        </div>
        <div style={{
          padding: "8px 16px", background: "var(--bg-primary)",
          borderTop: "1px solid var(--border-subtle)",
          fontSize: 11, color: "var(--text-tertiary)",
          display: "flex", justifyContent: "space-between",
        }}>
          <span>{t("Enter to pick · Esc to close")}</span>
          <span className="mono">{t("{a} vendors · {b} invoices", { a: vendors.length, b: invoices.length })}</span>
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{padding: "6px 8px"}}>
      <div style={{
        fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em",
        color: "var(--text-tertiary)", padding: "4px 12px 4px 12px",
      }}>{label}</div>
      {children}
    </div>
  );
}
function Row({ label, hint, onClick }) {
  return (
    <button onClick={onClick} style={{
      width: "100%", textAlign: "left",
      padding: "8px 12px",
      background: "transparent", border: "none",
      cursor: "pointer", borderRadius: 6,
      fontSize: 13, color: "var(--text-primary)",
      fontFamily: "var(--font-en)",
      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
    }}
      onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-elev-1)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      <span style={{flex: "0 0 auto", maxWidth: "55%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{label}</span>
      <span className="muted" style={{fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{hint}</span>
    </button>
  );
}

/* ⚠ MODIFIED FROM PORT — Edit profile modal. Calls api.updateVendor and
 * passes the updated record back so the caller can refresh + sync state. */
function EditVendorModal({ open, vendor, onClose, onSaved, isAdmin = false, pmUsers = [] }) {
  const t = useT();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (open && vendor) {
      setForm({
        name: vendor.name || '',
        cat: vendor.cat || 'prod',
        contact: vendor.contact || '',
        email: vendor.email || '',
        phone: vendor.phone || '',
        line_id: vendor.line_id || '',
        tax_id: vendor.tax_id || vendor.taxId || '',
        terms: vendor.terms || 'NET 30',
        bank_name: vendor.bank_name || '',
        account_number: vendor.account_number || '',
        account_name: vendor.account_name || '',
        credit_limit: vendor.credit_limit || vendor.creditLimit || '',
        address: vendor.address || '',
        city: vendor.city || '',
        country: vendor.country || 'TH',
        website: vendor.website || '',
        notes: vendor.notes || '',
        assigned_pm_email: vendor.assigned_pm_email || '',
      });
      setError(null);
      setBusy(false);
    }
  }, [open, vendor]);
  if (!open || !vendor) return null;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setError(null);
    if (!form.name.trim()) { setError('Company name is required.'); return; }
    if (!form.email.trim()) { setError('Contact email is required.'); return; }
    // Only police the account-number length when banking was actually edited,
    // so legacy rows with odd data don't block unrelated saves.
    const bankingTouched = form.bank_name !== (vendor.bank_name || '') || form.account_number !== (vendor.account_number || '');
    const expectDigits = bankAccountDigits(form.bank_name);
    const digits = form.account_number.replace(/\D/g, '');
    if (bankingTouched && expectDigits && digits.length > 0 && digits.length !== expectDigits) {
      setError(t('{bank} account numbers have {n} digits — you entered {m}.', { bank: t(form.bank_name), n: expectDigits, m: digits.length })); return;
    }
    setBusy(true);
    try {
      const vid = vendor.vendor_id || vendor.id;
      // Strip admin-only fields when not admin so the backend doesn't reject
      // the whole save with "only admin can change the assigned PM".
      const payload = { ...form };
      if (!isAdmin) delete payload.assigned_pm_email;
      // Credit limit is free text (see MoneyInput) — parse before sending;
      // blank clears it to 0, as before.
      const limit = String(form.credit_limit).trim() === '' ? 0 : parseMoney(form.credit_limit);
      if (!Number.isFinite(limit) || limit < 0) { setError('Credit limit must be a number (e.g. 2,500,000).'); return; }
      payload.credit_limit = limit;
      await api.updateVendor(vid, payload);
      window.__toast?.(`Updated ${form.name}`, 'success');
      onSaved?.({ ...vendor, ...payload, vendor_id: vid });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} width={680}>
      <ModalHeader title={t('Edit supplier profile')} subtitle={`${vendor.vendor_id || vendor.id} · ${vendor.name || ''}`} onClose={busy ? () => {} : onClose}/>
      <div style={{padding: "18px 22px", overflowY: "auto"}}>
        <div className="form-grid">
          <div className="field full">
            <label className="field-label">{t('Company name')}</label>
            <input className="input" value={form.name} onChange={set('name')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Category')}</label>
            <div className="select">
              <select value={form.cat} onChange={set('cat')}>
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{t(c.label)}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="field-label">{t('Credit terms')}</label>
            <div className="select">
              <select value={form.terms} onChange={set('terms')}>
                {["NET 7","NET 15","NET 30","NET 45","NET 60","NET 90"].map(tm => <option key={tm}>{tm}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="field-label">{t('Primary contact')}</label>
            <input className="input" value={form.contact} onChange={set('contact')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Email')}</label>
            <input className="input" value={form.email} onChange={set('email')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Phone')}</label>
            <input className="input" value={form.phone} onChange={set('phone')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('LINE ID')}</label>
            <input className="input mono" value={form.line_id} onChange={set('line_id')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Tax ID')}</label>
            <input className="input mono" value={form.tax_id} onChange={set('tax_id')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('City')}</label>
            <input className="input" value={form.city} onChange={set('city')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Country')}</label>
            <input className="input" value={form.country} onChange={set('country')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Website')}</label>
            <input className="input mono" value={form.website} onChange={set('website')}/>
          </div>
          <div className="field full">
            <label className="field-label">{t('Registered address')}</label>
            <textarea className="input" rows={2} value={form.address} onChange={set('address')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Bank')}</label>
            <div className="select">
              <select value={form.bank_name} onChange={set('bank_name')}>
                <option value="">{t('— Select bank —')}</option>
                {BANKS.map(b => <option key={b} value={b}>{t(b)}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="field-label">{t('Account number')}</label>
            <input className="input mono" inputMode="numeric" value={form.account_number} onChange={set('account_number')} placeholder={bankAccountPlaceholder(form.bank_name)}/>
            <span className="field-help">
              {bankAccountDigits(form.bank_name) === 12 ? t("Government Savings Bank accounts are 12 digits (dashes optional).")
                : bankAccountDigits(form.bank_name) === 10 ? t("10 digits (dashes optional).") : t("As printed on your bank book.")}
            </span>
          </div>
          <div className="field full">
            <label className="field-label">{t('Account name')}</label>
            <input className="input" value={form.account_name} onChange={set('account_name')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Credit limit (THB)')}</label>
            <MoneyInput className="input mono" value={form.credit_limit} onChange={set('credit_limit')}/>
          </div>
          <div className="field full">
            <label className="field-label">{t('Notes')}</label>
            <textarea className="input" rows={2} value={form.notes} onChange={set('notes')}/>
          </div>
          {isAdmin && (
            <div className="field full">
              <label className="field-label">
                {t('Assigned PM')}
                <span className="muted" style={{marginLeft: 8, fontWeight: 400, fontSize: 11}}>
                  {t('Routes PM-review invoices for this supplier · admin-only')}
                </span>
              </label>
              <div className="select">
                <select value={form.assigned_pm_email || ''} onChange={set('assigned_pm_email')}>
                  <option value="">{t('— Unassigned (any PM can review) —')}</option>
                  {pmUsers.map(u => (
                    <option key={u.email} value={u.email}>{u.name || u.email} · {u.email}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
        {error && (
          <div style={{marginTop: 14, padding: "10px 12px", background: "color-mix(in oklab, var(--danger) 12%, transparent)", color: "var(--danger)", borderRadius: 8, fontSize: 13}}>
            {error}
          </div>
        )}
      </div>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t('Cancel')}</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>
          {busy ? t('Saving…') : t('Save changes')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ⚠ MODIFIED FROM PORT — standalone "New review" modal for the Quality screen.
 * Picks a vendor, scores the four KPIs, and submits a rating without changing
 * any invoice status. */
function NewReviewModal({ open, vendors, onClose, onSubmitted }) {
  const t = useT();
  const [vendorId, setVendorId] = useState("");
  const [project, setProject] = useState("");
  const [scores, setScores] = useState({ quality: 5, timeliness: 5, comms: 5, value: 5 });
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setVendorId(""); setProject("");
      setScores({ quality: 5, timeliness: 5, comms: 5, value: 5 });
      setComment(""); setBusy(false);
    }
  }, [open]);
  if (!open) return null;

  const submit = async () => {
    if (!vendorId)         { window.__toast?.('Pick a vendor', 'error'); return; }
    if (!project.trim())   { window.__toast?.('Project is required', 'error'); return; }
    setBusy(true);
    try {
      await api.submitRating({
        vendor_id: vendorId,
        project: project.trim(),
        quality_5: scores.quality,
        timeliness_5: scores.timeliness,
        comms_5: scores.comms,
        value_5: scores.value,
        comment,
      });
      window.__toast?.('Rating submitted', 'success');
      onSubmitted?.();
    } catch (e) {
      window.__toast?.(`Failed: ${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const Slider = ({ label, value, onChange }) => (
    <div className="field">
      <label className="field-label" style={{display: "flex", justifyContent: "space-between"}}>
        <span>{label}</span>
        <span className="mono" style={{color: "var(--accent)"}}>{value} / 5</span>
      </label>
      <input type="range" min="1" max="5" step="1" value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: "var(--accent)" }}/>
    </div>
  );

  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} width={560}>
      <ModalHeader title={t('New quality review')} subtitle={t('Score a supplier at project closure')} onClose={busy ? () => {} : onClose}/>
      <div style={{padding: "18px 22px", overflowY: "auto"}}>
        <div className="form-grid">
          <div className="field full">
            <label className="field-label">{t('Supplier')} <span style={{color:"var(--danger)"}}>*</span></label>
            <div className="select">
              <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                <option value="">{t('— Select supplier —')}</option>
                {vendors.filter(v => v.status === 'active').map(v => (
                  <option key={v.vendor_id || v.id} value={v.vendor_id || v.id}>
                    {v.name} · {v.vendor_id || v.id}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field full">
            <label className="field-label">{t('Project')} <span style={{color:"var(--danger)"}}>*</span></label>
            <input className="input" value={project} onChange={(e) => setProject(e.target.value)}
              placeholder="Toyota Bz4X campaign"/>
          </div>
          <Slider label={t('Quality of work')}  value={scores.quality}    onChange={v => setScores(s => ({...s, quality: v}))}/>
          <Slider label={t('Timeliness')}       value={scores.timeliness} onChange={v => setScores(s => ({...s, timeliness: v}))}/>
          <Slider label={t('Communication')}    value={scores.comms}      onChange={v => setScores(s => ({...s, comms: v}))}/>
          <Slider label={t('Value for money')}  value={scores.value}      onChange={v => setScores(s => ({...s, value: v}))}/>
          <div className="field full">
            <label className="field-label">{t('Comment')}</label>
            <textarea className="input" rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder={t('What went well, what could improve…')}/>
          </div>
        </div>
      </div>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t('Cancel')}</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>
          {busy ? t('Submitting…') : t('Submit review')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function NavItem({ item, active, onClick }) {
  const t = useT();
  const I = item.icon;
  return (
    <div className={"nav-item " + (active ? "active" : "")} onClick={onClick}>
      <I className="nav-icon" size={15}/>
      <span>{t(item.label)}</span>
      {item.count !== undefined && <span className="nav-count">{item.count}</span>}
    </div>
  );
}


function PlaceholderScreen({ label }) {
  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{label}</h1>
          <div className="sub">Screen scaffold — wire up next iteration</div>
        </div>
      </div>
      <div style={{padding: 24, display:"grid", placeItems:"center", flex:1, color:"var(--text-tertiary)"}}>
        Coming next — {label}
      </div>
    </div>
  );
}

function PortalDashboard({ onSubmit, onBilling, vendor, invoices }) {
  const t = useT();
  // ⚠ MODIFIED FROM PORT — every tile and section now computes from this
  // vendor's live `invoices` array. No hardcoded amounts.
  const bills = Array.isArray(invoices) ? invoices : [];
  const amountOf = (b) => netPayableOf(b);
  const pending  = bills.filter(b => ["pm-review","finance-verify"].includes(b.status));
  const upcoming = bills.filter(b => ["approved-payment","scheduled"].includes(b.status));
  const needs    = bills.filter(b => ["disputed","rejected"].includes(b.status));
  const thisYear = new Date().getFullYear();
  const lastYear = thisYear - 1;
  const paidYtd  = bills.filter(b => b.status === "paid" && b.paid_at && new Date(b.paid_at).getFullYear() === thisYear)
                        .reduce((a, b) => a + amountOf(b), 0);
  const paidLy   = bills.filter(b => b.status === "paid" && b.paid_at && new Date(b.paid_at).getFullYear() === lastYear)
                        .reduce((a, b) => a + amountOf(b), 0);
  const ytdDelta = paidLy > 0 ? Math.round(((paidYtd - paidLy) / paidLy) * 100) : null;

  // Next payment = the first scheduled invoice, or the next approved-payment one
  // sorted by payment_scheduled / due_at ascending.
  const sortedUpcoming = upcoming.slice().sort((a, b) => {
    const ta = new Date(a.payment_scheduled || a.due_at || 0).getTime();
    const tb = new Date(b.payment_scheduled || b.due_at || 0).getTime();
    return ta - tb;
  });
  const nextPay = sortedUpcoming[0];
  const nextPayWhen = nextPay ? new Date(nextPay.payment_scheduled || nextPay.due_at || 0) : null;
  const nextPayLabel = nextPayWhen && !Number.isNaN(nextPayWhen.getTime())
    ? nextPayWhen.toLocaleDateString(undefined, { month: 'short', day: '2-digit' })
    : null;

  // Avg days to pay = mean cycle for paid invoices we have submission+paid dates for.
  const cycles = bills
    .filter(b => b.status === 'paid' && b.submitted_at && b.paid_at)
    .map(b => (new Date(b.paid_at).getTime() - new Date(b.submitted_at).getTime()) / 86400000);
  const avgCycle = cycles.length ? (cycles.reduce((a, b) => a + b, 0) / cycles.length) : null;
  const termsDaysMatch = String(vendor?.terms || '').match(/(\d+)/);
  const termsDays = termsDaysMatch ? Number(termsDaysMatch[1]) : null;
  const cycleVsTerms = (avgCycle !== null && termsDays)
    ? (avgCycle < termsDays ? `Better than ${vendor.terms} terms` : `Above ${vendor.terms} terms`)
    : (avgCycle !== null ? 'across paid invoices' : 'no paid invoices yet');

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Hello, {name} 👋", { name: vendor?.name || t('Vendor') })}</h1>
          <div className="sub">
            {t("{n} invoices on file", { n: bills.length })} ·{' '}
            {nextPay ? t("next payment {date}", { date: nextPayLabel || 'TBD' }) : t('no upcoming payments')}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary btn-sm" onClick={onBilling}>{t("View all invoices")}</button>
          <button className="btn btn-primary btn-sm" onClick={onSubmit}><IconUpload size={14}/> {t("Submit new invoice")}</button>
        </div>
      </div>

      <div className="stat-row">
        <div className="stat-tile">
          <div className="label">{t("Next payment")}</div>
          <div className="val tnum" style={{color: nextPay ? "var(--text-accent)" : "var(--text-tertiary)"}}>
            {nextPay ? fmtTHB(amountOf(nextPay)) : '—'}
          </div>
          <div className="delta muted">
            {nextPay
              ? `${nextPayLabel || 'TBD'} · ${nextPay.invoice_id || nextPay.id}`
              : t('nothing scheduled')}
          </div>
        </div>
        <div className="stat-tile">
          <div className="label">{t("In review")}</div>
          <div className="val tnum">{fmtTHB(pending.reduce((a, b) => a + amountOf(b), 0))}</div>
          <div className="delta muted">{t("{n} invoices", { n: pending.length })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t("Paid YTD")}</div>
          <div className="val tnum">{fmtTHB(paidYtd)}</div>
          <div className="delta muted">
            {ytdDelta === null
              ? (paidLy === 0 ? t('first year') : '—')
              : `${ytdDelta >= 0 ? '+' : ''}${ytdDelta}% ${t('vs last year')}`}
          </div>
        </div>
        <div className="stat-tile">
          <div className="label">{t("Avg days to pay")}</div>
          <div className="val tnum">
            {avgCycle === null ? '—' : <>{avgCycle.toFixed(1)}<span style={{fontSize:18, color:"var(--text-tertiary)"}}>d</span></>}
          </div>
          <div className="delta muted">{cycleVsTerms}</div>
        </div>
      </div>

      <div style={{padding: "0 24px 24px", display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16}}>
        <div className="section">
          <div className="section-head"><h3>{t('Needs your attention')}</h3>
            <span className="muted" style={{fontSize:12}}>{t('{n} items', { n: needs.length })}</span>
          </div>
          <div>
            {needs.length === 0 ? (
              <div style={{padding: "24px 18px", color: "var(--text-tertiary)", fontSize: 13}}>
                {t("You're all caught up — nothing rejected or disputed.")}
              </div>
            ) : needs.map(b => (
              <div key={b.invoice_id || b.id} className="approval-row">
                <div className="approval-meta">
                  <div className="approval-title">
                    <span className="mono">{b.invoice_id || b.id}</span> · {b.project || '—'}
                  </div>
                  <div className="approval-sub">{fmtTHB(amountOf(b))} · {t('submitted {age}', { age: b.age || '' })}</div>
                  <div className="approval-chips">
                    <StatusPill meta={BILLING_STATUS_META[b.status]}/>
                  </div>
                </div>
                <div className="approval-actions">
                  <button className="btn btn-secondary btn-sm" onClick={onBilling}>{t('Open')}</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="section">
          <div className="section-head"><h3>{t('Upcoming payments')}</h3></div>
          <div className="section-body" style={{display:"flex", flexDirection:"column", gap: 14}}>
            {sortedUpcoming.length === 0 ? (
              <div style={{color: "var(--text-tertiary)", fontSize: 13}}>
                {t('No payments scheduled yet.')}
              </div>
            ) : sortedUpcoming.map(b => {
              const when = new Date(b.payment_scheduled || b.due_at || 0);
              const whenLabel = !Number.isNaN(when.getTime())
                ? when.toLocaleDateString(undefined, { month: 'short', day: '2-digit' })
                : 'TBD';
              return (
                <div key={b.invoice_id || b.id} style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start"}}>
                  <div>
                    <div style={{fontSize: 13}}>{b.project || '—'}</div>
                    <div className="muted" style={{fontSize: 11, marginTop: 2, fontFamily: "var(--font-mono)"}}>{b.invoice_id || b.id} · {t('pay by {date}', { date: whenLabel })}</div>
                  </div>
                  <div style={{fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 500, fontVariantNumeric: "tabular-nums"}}>{fmtTHB(amountOf(b))}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ⚠ Original prototype called ReactDOM.createRoot(...).render(<App/>) here —
// removed because main.jsx now mounts the app.
